import { useEffect, useState } from 'preact/hooks';
import Router, { route, Route, type RouterOnChangeArgs } from 'preact-router';
import { setUnauthorizedHandler } from '@/lib/api.ts';
import { isCryptoAvailable } from '@/lib/crypto.ts';
import { ensureClientSessionId, session } from '@/lib/session.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { hydrateAuth, signOut } from '@/store/auth.slice.ts';
import { initAnalytics, trackPageView } from '@/lib/analytics.ts';
import { setPreviousPath } from '@/store/ui.slice.ts';
import { onAppEvent } from '@/lib/app-bridge.ts';
import { requestPushPermission, syncPush } from '@/lib/push.ts';
import { Modal } from '@/components/modal.tsx';
import { AppOpenBanner } from '@/components/app-open-banner.tsx';
import Business from '@/pages/business.tsx';
import Event from '@/pages/event.tsx';
import EventView from '@/pages/event-view.tsx';
import Home from '@/pages/home.tsx';
import Info from '@/pages/info.tsx';
import InfoCode from '@/pages/info-code.tsx';
import Legal from '@/pages/legal.tsx';
import Login from '@/pages/login.tsx';
import Mission from '@/pages/mission.tsx';
import Mypage from '@/pages/mypage.tsx';
import Play from '@/pages/play.tsx';
import MypageAccount from '@/pages/mypage-account.tsx';
import MypageEdit from '@/pages/mypage-edit.tsx';
import MypageHistory from '@/pages/mypage-history.tsx';
import MypageWithdraw from '@/pages/mypage-withdraw.tsx';
import NotFound from '@/pages/not-found.tsx';
import OauthKakao from '@/pages/oauth/kakao.tsx';
import OauthNaver from '@/pages/oauth/naver.tsx';
import WebzineIssue from '@/pages/webzine-issue.tsx';
import WebzineList from '@/pages/webzine-list.tsx';

export function App() {
  const dispatch = useAppDispatch();
  const hydrated = useAppSelector((state) => state.auth.hydrated);
  const userId = useAppSelector((state) => state.auth.user?.id);
  /** 지금 경로 — '앱에서 열기' 배너가 화면마다 다시 판단하도록 */
  const [path, setPath] = useState(() => location.pathname);

  useEffect(() => {
    // 유입 주소(`/f/<코드>/...`)는 **여기서 처리하지 않는다.** 앱이 그려지기 전인
    // main.tsx 에서 주소를 미리 바꿔 둔다 — 이 자리에서는 이미 늦다(inflow.ts 주석 참고).

    // ★ crypto.subtle 은 보안 컨텍스트(https / localhost)에서만 쓸 수 있다.
    //   평문 http + 커스텀 도메인이면 세션 저장과 API 본문 암호화가 전부 실패하는데,
    //   증상은 "로그인이 안 풀리고 계속 로그인 화면으로 되돌아옴" 으로만 보인다.
    if (!isCryptoAvailable()) {
      console.error(
        `[urnplay] crypto.subtle 을 쓸 수 없습니다. 현재 주소: ${location.origin}
` +
          'https 로 접속하거나 http://localhost 를 사용하세요. ' +
          '이 상태로는 로그인 유지와 회원 API 가 동작하지 않습니다.'
      );
    }

    // 분석 도구. 측정 ID 가 없으면 아무것도 하지 않는다.
    initAnalytics();
    trackPageView(`${location.pathname}${location.search}`);

    // 비로그인 사용자 식별 토큰 확보
    window.sessionid = ensureClientSessionId();

    // 만료된 세션 항목 정리 후 로그인 정보 복원
    session.cleanupExpired();
    void dispatch(hydrateAuth());

    // 401 이 오면 전역에서 한 번만 로그아웃 처리한다.
    setUnauthorizedHandler(() => {
      dispatch(signOut());
      route('/login', true);
    });

    return () => setUnauthorizedHandler(null);
  }, [dispatch]);

  /*
    앱 푸시 기기 등록. (2026-10-08)

    로그인 정보를 복원한 뒤, 그리고 로그인·로그아웃할 때마다 다시 등록한다. 서버는
    이번 요청의 로그인 회원을 기기에 붙이므로, 로그아웃 뒤에 등록하면 회원이 떨어진다.
    일반 브라우저에서는 아무 일도 없다.

    ★ 알림 권한(Android 13+)은 **로그인한 뒤 한 번만** 묻는다. 처음 켜자마자 물으면
      무엇을 알려 줄지 모르는 채로 거절하기 쉽다. 거절해도 마이페이지에서 다시 켤 수 있다.
  */
  useEffect(() => {
    if (!hydrated) return;

    void (async () => {
      if (userId && !localStorage.getItem('app:push-asked')) {
        localStorage.setItem('app:push-asked', '1');
        await requestPushPermission();
      }
      await syncPush();
    })();
  }, [hydrated, userId]);

  // 앱이 FCM 토큰이 바뀌었다고 알리면 다시 등록한다.
  useEffect(() => onAppEvent('pushToken', () => void syncPush()), []);

  const handleRouteChange = (e: RouterOnChangeArgs) => {
    dispatch(setPreviousPath(e.previous ?? null));
    setPath(e.url);

    // SPA 는 문서가 다시 뜨지 않는다. 화면이 바뀔 때마다 직접 알려야 집계된다.
    trackPageView(e.url);
  };

  // 세션 복원 전에는 아무것도 그리지 않는다 (로그인 상태 깜빡임 방지).
  if (!hydrated) return null;

  return (
    <>
      <Modal />
      <Router onChange={handleRouteChange}>
        <Home path="/" />
        <Login path="/login" />
        <Info path="/info" />
        <InfoCode path="/info/code" />
        <Play path="/play/:id" />
        <MypageAccount path="/mypage/account" />
        <MypageEdit path="/mypage/edit" />
        <MypageHistory path="/mypage/history" />
        <MypageWithdraw path="/mypage/withdraw" />
        <Mypage path="/mypage" />
        <WebzineList path="/webzine" />
        <WebzineIssue path="/webzine/:vol" />
        <Mission path="/mission" />
        <EventView path="/event/:id" />
        <Event path="/event" />

        {/* 약관·방침. 시안은 팝업 한 개의 2단계지만 주소를 나눴다 (legal.tsx 주석 참고) */}
        <Legal path="/terms" />
        <Legal path="/service" doc="service" />
        <Legal path="/privacy" doc="privacy" />
        <Legal path="/consent" doc="consent" />
        <Business path="/business" />

        {/* 소셜 로그인 팝업 콜백 */}
        <OauthNaver path="/oauth/naver.callback" />
        <OauthKakao path="/oauth/kakao.callback" />

        {/*
          .html/ 시안 기준 이식 대기 라우트.
          탭바 5개: / (홈) · /webzine · /event · /mission · /mypage


          시안에 없고 구 프로젝트에서 가져와야 하는 것:
            <SCGate         path="/sc/:serviceid/" />
        */}

        <Route default component={NotFound} />
      </Router>
      <AppOpenBanner path={path} />
    </>
  );
}
